/**
 * Language and country pickers of the header (snippets/mm-header-localization.liquid).
 *
 * The options are submit buttons of plain localization forms, so choosing one already
 * works without this file. What it adds is the behaviour of note 8 of the design:
 *
 * - `header`: each trigger opens its popover, one at a time. Esc, a click outside and a
 *   second click close it, and the focus goes back to the trigger.
 * - `drawer`: either trigger opens the bottom sheet, a modal dialog, which the browser
 *   closes on Esc; the X and a tap on the dim backdrop close it too.
 * - Both: the search field filters the countries, ignoring case and accents, and the
 *   arrow keys move through the options of a list.
 */
class MmHeaderLocalization extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** Set when Esc closes the sheet, so that same key does not close the menu too. */
  #swallowEscape = false;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    this.addEventListener('input', this.#onInput, { signal });
    document.addEventListener('click', this.#onDocumentClick, { signal });

    this.querySelector('[data-sheet]')?.addEventListener('close', this.#onSheetClose, { signal });
    this.querySelector('[data-sheet]')?.addEventListener('cancel', this.#onSheetCancel, { signal });
    this.addEventListener('keyup', this.#onKeyup, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  get #isDrawer() {
    return this.dataset.context === 'drawer';
  }

  /** @returns {HTMLDialogElement | null} */
  get #sheet() {
    return this.querySelector('[data-sheet]');
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {Element} */ (event.target);

    const trigger = target.closest('[data-trigger]');
    if (trigger instanceof HTMLButtonElement) {
      if (this.#isDrawer) this.#openSheet(trigger);
      else this.#togglePopover(trigger);
      return;
    }

    if (target.closest('[data-close]')) {
      this.#sheet?.close();
      return;
    }

    // A tap on the backdrop lands on the dialog itself, outside its padding box.
    const sheet = this.#sheet;
    if (sheet && target === sheet) {
      const box = sheet.getBoundingClientRect();
      const inside =
        event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
      if (!inside) sheet.close();
    }
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (this.#isDrawer) return;
    if (this.contains(/** @type {Node} */ (event.target))) return;
    this.#closePopovers();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape' && !this.#isDrawer) {
      const open = this.querySelector('[data-trigger][aria-expanded="true"]');
      if (open instanceof HTMLElement) {
        event.preventDefault();
        this.#closePopovers();
        open.focus();
      }
      return;
    }

    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;

    const target = /** @type {Element} */ (event.target);
    const scope = target.closest('[data-popover], [data-sheet]');
    if (!scope) return;

    const options = /** @type {HTMLElement[]} */ (
      [...scope.querySelectorAll('[role="option"]')].filter((option) => !option.hasAttribute('hidden'))
    );
    if (!options.length) return;

    event.preventDefault();
    const index = options.indexOf(/** @type {HTMLElement} */ (target));
    let next = 0;
    if (event.key === 'End') next = options.length - 1;
    else if (event.key === 'ArrowDown') next = index < 0 ? 0 : Math.min(index + 1, options.length - 1);
    else if (event.key === 'ArrowUp') next = index <= 0 ? 0 : index - 1;
    options[next]?.focus();
  };

  /** @param {Event} event */
  #onInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.hasAttribute('data-search')) return;

    const scope = input.closest('[data-popover], [data-sheet]');
    if (!scope) return;

    const query = normalize(input.value);
    let shown = 0;
    for (const option of scope.querySelectorAll('.mm-hloc__list--countries [role="option"]')) {
      const match = !query || normalize(option.getAttribute('data-name') || '').includes(query);
      option.toggleAttribute('hidden', !match);
      if (match) shown += 1;
    }
    scope.querySelector('[data-empty]')?.toggleAttribute('hidden', shown > 0);
  };

  /** @param {HTMLButtonElement} trigger */
  #togglePopover(trigger) {
    const wasOpen = trigger.getAttribute('aria-expanded') === 'true';
    this.#closePopovers();
    if (wasOpen) return;

    const popover = this.querySelector(`[data-popover="${trigger.dataset.trigger}"]`);
    if (!(popover instanceof HTMLElement)) return;

    this.#placePopovers();
    popover.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');

    const search = popover.querySelector('[data-search]');
    const selected = popover.querySelector('[aria-selected="true"]');
    if (search instanceof HTMLElement) search.focus();
    else if (selected instanceof HTMLElement) selected.focus();
  }

  #closePopovers() {
    for (const popover of this.querySelectorAll('[data-popover]')) {
      if (popover instanceof HTMLElement) popover.hidden = true;
    }
    for (const trigger of this.querySelectorAll('[data-trigger]')) {
      trigger.setAttribute('aria-expanded', 'false');
    }
  }

  /**
   * The popovers hang from the bottom edge of the header (note 5), not from the
   * triggers, so their top is the distance between the two.
   */
  #placePopovers() {
    const header = this.closest('header-component, .header, header') || document.querySelector('#header-component');
    if (!header) return;
    const offset = header.getBoundingClientRect().bottom - this.getBoundingClientRect().top - 1;
    this.style.setProperty('--mm-hloc-top', `${Math.round(offset)}px`);
  }

  /** @param {HTMLButtonElement} trigger */
  #openSheet(trigger) {
    const sheet = this.#sheet;
    if (!sheet || sheet.open) return;

    // The browser puts the focus on the first control, the X. Not on the search
    // field: on a phone that would throw the keyboard over the list at once.
    sheet.showModal();
    for (const button of this.querySelectorAll('[data-trigger]')) button.setAttribute('aria-expanded', 'true');
    this.dataset.opener = trigger.dataset.trigger;
  }

  // The browser closes the dialog on keydown; header-drawer.js closes the whole menu on
  // the keyup that follows (assets/header-drawer.js:34), which by then lands on the
  // trigger, inside this element. It stops here.
  #onSheetCancel = () => {
    this.#swallowEscape = true;
  };

  /** @param {KeyboardEvent} event */
  #onKeyup = (event) => {
    if (event.key !== 'Escape' || !this.#swallowEscape) return;
    this.#swallowEscape = false;
    event.stopPropagation();
  };

  #onSheetClose = () => {
    for (const button of this.querySelectorAll('[data-trigger]')) button.setAttribute('aria-expanded', 'false');
    const opener = this.querySelector(`[data-trigger="${this.dataset.opener}"]`);
    if (opener instanceof HTMLElement) opener.focus();
  };
}

/**
 * Lower case without accents, so «mexico» finds «México».
 * @param {string} value
 */
function normalize(value) {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

if (!customElements.get('mm-header-localization')) {
  customElements.define('mm-header-localization', MmHeaderLocalization);
}
